import { Edge, Node } from 'reactflow';

import {
    SegmentDefinition,
    SegmentEngagement,
    SegmentFilterEngagementFields,
    SegmentFilterState,
} from 'domains/segments/types';
import {
    CampaignStatus,
    CampaignType,
    CurationNavState,
    CurationSegmentsAction,
    SendJobStatus,
    URLPaths,
} from 'models/enums';
import { getCamelToKebabCase } from 'utils';

export enum AudienceType {
    AUDIENCE = 'audience',
    COHORT = 'cohort',
}

export enum SendRecurrenceFrequency {
    CONTINUOUS = 'continuous',
    DOES_NOT_REPEAT = 'does not repeat',
    DAILY = 'daily',
    WEEKLY = 'weekly',
    EVERY_WEEKDAY = 'every weekday',
    MONTHLY = 'monthly',
}

export const iSegmentsActionState = (segmentsAction: CurationState): segmentsAction is SegmentsActionState =>
    Boolean((segmentsAction as SegmentsActionState)?.action);

export type Audience = {
    id: number;
    cohortId?: string;
    parentAudienceDelayInterval?: string;
    parentAudienceId?: number;
};

export type ABTestResult = {
    sendId: number;
    openRate: string;
    clickRate: string;
    totalSent: string;
    totalOpened: string;
    totalClicked: string;
    uniqueOpened: string;
    deliveredRate: string;
    uniqueClicked: string;
    totalDelivered: string;
    uniqueOpenRate: string;
    uniqueClickRate: string;
};

export enum ABTestVariant {
    SENDER_PROFILE = 'Sender profile',
    BODY = 'Body',
    PREVIEW_TEXT = 'Preview text',
    SUBJECT_LINE = 'Subject line',
    SCHEDULE_TIME = 'Schedule time',
}

export type ABTestConfig = {
    testOn?: ABTestType;
    testPercentage: number;
    testEndsOn?: string;
    winnerSendId?: number;
    winnerDeclaredAt?: string;
    winnerDeclaredBy?: string;
    testResults?: Record<string | number, ABTestResult>;
    comparisonFields?: ABTestVariant[];
};

export type Campaign<SendT = Send> = {
    audiences?: Audience[];
    id: string;
    name: string;
    type: CampaignType;
    // 'MIXED' when the campaign's sends span more than one channel (backend-derived; see
    // outreach-backend's MIXED_CHANNEL). Individual sends always have one real channel — see
    // Send.channel below — so per-send logic should never need to check for MIXED.
    channel: CampaignChannels | typeof MIXED_CHANNEL;
    status: CampaignStatus;
    startTime: string;
    endTime: string;
    sends: SendT[];
    canArchive?: boolean;
    canCancel: boolean;
    canDelete: boolean;
    canDraft?: boolean;
    canEdit?: boolean;
    canPause: boolean;
    canResume: boolean;
    canStop: boolean;
    isArchived?: boolean;
    createdAt?: string;
    lastModifiedTimestamp?: string;
    deletedAt?: string;
    recurrenceFrequency?: string;
    recurrenceDayOfWeek?: string | null;
    recurrenceOccurrencePosition?: number | null;
    reentryDetails?: ReEntryFields;
    // Campaign-level exit criteria: the segment definition snapshotted onto the campaign.
    // exitSegmentDefinition is the field name returned by the campaign list API (c.* column).
    // exitCriteria is the field name returned by the draft builder API (GET /campaigns/draft/:id).
    exitSegmentDefinition?: SegmentDefinition | null;
    exitCriteria?: ExitCriteriaFields | null;
    abTestConfig?: ABTestConfig;
    structure?: CampaignStructureItem[];
};

/**
 * For now the only campaign type that is not supported for the graph based state is conditional split.
 */
export enum CampaignKey {
    SINGLE_SEND = 'singleSend',
    EMAIL_SERIES = 'emailSeries',
}

/**
 * The API does not return the all the fields for Send in GET /campaigns.
 */
export type PartialSend = {
    id: number;
    recurrenceFrequency?: SendRecurrenceFrequency;
    scheduledEndTimestamp?: string;
    scheduledTimestamp: string;
};

export type Send = {
    audienceId?: number;
    campaignId: string;
    channel: CampaignChannels;
    cohortId?: string;
    contentSnapshot: string;
    contentSnapshotDefaultTemplateData: object;
    contentSnapshotTemplateFields: string;
    directMailContentId?: number;
    directMailSenderProfileId?: number;
    editorContentSnapshot: string;
    emailSenderProfileId: number;
    emailTemplateId: number;
    executionArn: string;
    id: number;
    isActive: boolean;
    name: string;
    preHeader: string;
    scheduledTimestamp: string;
    segmentId: number;
    sendClassification: string;
    sendDefinitionId: number;
    smsContentId?: number;
    smsSenderProfileId?: number;
    segmentDefinition?: SegmentDefinition;
    segments?: SegmentEngagement[];
    splitAction?: SplitActions;
    splitType?: SplitTypes;
    status: SendJobStatus;
    subjectLine: string;
    createdTimestamp?: string;
    deletedAt?: string;
    lastModifiedTimestamp?: string;
    parentAudienceDelayInterval?: string;
    parentId?: number;
    pickupTimestamp?: string;
    recurrenceFrequency?: SendRecurrenceFrequency;
    recurrenceDayOfWeek?: string | null;
    recurrenceOccurrencePosition?: number | null;
    scheduledEndTimestamp?: string;
    callerId?: VoiceCallerDetails;
    transferNumber?: string;
    voiceSenderProfileId?: number;
    voiceContentId?: number;
};

export type VoiceCallerDetails = {
    name: string;
    phoneNumber: string;
};

export enum CampaignChannels {
    DIRECT_MAIL = 'DIRECTMAIL',
    EMAIL = 'EMAIL',
    NOOP = 'NOOP',
    SMS = 'SMS',
    VOICE_CALL = 'VOICE',
    WHATSAPP = 'WHATSAPP',
}

/**
 * Sentinel used only to start a new campaign that will mix channels across its sends (e.g.
 * Email + SMS). Not a real send channel — deliberately kept out of the CampaignChannels enum
 * so per-send channel switches stay exhaustive. Mirrors the backend's MIXED_CHANNEL constant.
 */
export const MIXED_CHANNEL = 'MIXED' as const;

export const CampaignChannelLabels: { [key in CampaignChannels]: string } = {
    [CampaignChannels.DIRECT_MAIL]: 'direct mail',
    [CampaignChannels.EMAIL]: 'email',
    [CampaignChannels.NOOP]: 'no operation',
    [CampaignChannels.SMS]: 'text message',
    [CampaignChannels.VOICE_CALL]: 'voice call',
    [CampaignChannels.WHATSAPP]: 'whatsapp message',
};

/**
 * State types
 */
export type CurationState = {
    navState: CurationNavState;
    /**
     * True if the form is complete regardless of the active route.
     * Different than CurationNavState.COMPLETED because when the form is complete but the user is on that route, then the state is IN_PROGRESS_CURRENT
     */
    isComplete: boolean;
};
export type AudienceState = CurationState & {
    audienceId: number | null;
};
export type ContentState = CurationState & {
    preheader: string;
    senderProfileId: number | null;
    subjectLine: string;
    templateId: number | null;
};
export type DelayState = CurationState & {
    delay: number;
    unit: DelayUnit;
    errorMessage: string;
};
export type MetadataState = CurationState & {
    curationName: string;
    previewContentId: number | null;
    urlPath: URLPaths.CAMPAIGNS | URLPaths.CURATIONS;
};
export type PublishState = CurationState & {
    /**
     * The curation is not considered complete until the publish button is clicked.
     * Otherwise the RouteLeavingGuard will not prompt the user when they navigate away on the publish page.
     */
    publishClicked: boolean;
};
export type ScheduleState = CurationState & {
    isDatetimeError: boolean;
    isRecommendedTimeEnabled: boolean;
    scheduledTimestamp: string | null;
    recurrenceFrequency?: SendRecurrenceFrequency;
    scheduledEndTimestamp?: string;
    recurrenceDayOfWeek?: string | null;
    recurrenceOccurrencePosition?: number | null;
};
export type SegmentsActionState = CurationState & {
    action: CurationSegmentsAction;
};
export type SegmentsState = CurationState & {
    segments: SegmentEngagement[];
};

/**
 * Payload types
 */
export type EditAudiencePayload = {
    audienceId: number;
};
export type EditContentPayload = {
    preheader: string;
    senderProfileId: number | null;
    subjectLine: string;
    templateId: number | null;
};
export type EditDelayPayload = {
    delay: number;
    unit: DelayUnit;
    errorMessage: string;
};
export type EditMetadataPayload = {
    curationName?: string;
    previewContentId?: number | null;
    urlPath?: URLPaths.CAMPAIGNS | URLPaths.CURATIONS;
};
export type EditSchedulePayload = {
    isRecommendedTimeEnabled: boolean;
    scheduledTimestamp: string | null;
    recurrenceFrequency?: SendRecurrenceFrequency;
    scheduledEndTimestamp?: string;
    recurrenceDayOfWeek?: string | null;
    recurrenceOccurrencePosition?: number | null;
};
export type EditSegmentsPayload = {
    segments: SegmentEngagement[];
};
export type EditSegmentsActionPayload = {
    action: CurationSegmentsAction;
};

export type Email = {
    contentSnapshot: string;
    fromAddress: string;
    preHeader: string;
    subjectLine: string;
    scheduledTimestamp: string;
    templateName: string;
    sendName?: string;
};

export type DirectMailSendInput = {
    channel: CampaignChannels.DIRECT_MAIL;
    cohortId?: string;
    contentId: number;
    name: string;
    scheduledTimestamp?: string;
    segmentId: number;
    senderProfileId: number;
};

export type EmailSendInput = {
    channel: CampaignChannels.EMAIL;
    cohortId?: string;
    name: string;
    parentId?: number;
    parentIndex?: number;
    parentAudienceDelayInterval?: string;
    preHeader: string;
    scheduledTimestamp?: string;
    recurrenceFrequency?: SendRecurrenceFrequency;
    recurrenceDayOfWeek?: string | null;
    recurrenceOccurrencePosition?: number | null;
    scheduledEndTimestamp?: string;
    senderProfileId: number;
    subjectLine: string;
    templateId: number;
    segmentId?: number;
    segments?: SegmentEngagement[];
    segmentDefinition?: SegmentDefinition;
    splitAction?: SplitActions;
    splitType?: SplitTypes;
    // Frontend-only editing aid persisted via draft_structure so nested conditional splits
    // can be re-detected on reload. Not used by the backend's normalized send records.
    splitNodeId?: string;
};

export type SmsSendInput = {
    channel: CampaignChannels.SMS;
    cohortId?: string;
    name: string;
    smsContentId: number;
    recurrenceFrequency?: SendRecurrenceFrequency;
    recurrenceDayOfWeek?: string | null;
    recurrenceOccurrencePosition?: number | null;
    scheduledEndTimestamp?: string;
    scheduledTimestamp: string;
    segmentId?: number;
    smsSenderProfileId?: number;
};

export type SmsSendInputLegacy = {
    channel: CampaignChannels.SMS;
    name: string;
    content: string;
    recurrenceFrequency?: SendRecurrenceFrequency;
    scheduledEndTimestamp?: string;
    scheduledTimestamp: string;
    segmentId?: number;
};

export type ExitActionInput = {
    channel: CampaignChannels.NOOP;
    parentIndex: number;
    splitAction: SplitActions.EXIT;
    splitType: SplitTypes;
    // Optional fields to carry split criteria and time delay metadata for NOOP sends
    // This allows the UI to properly render the split criteria and time delay metadata
    parentAudienceDelayInterval?: string;
    segmentId?: number;
    segments?: SegmentEngagement[];
    segmentDefinition?: SegmentDefinition;
    splitNodeId?: string;
};

export type VoiceCallSendInput = {
    callerProfileId: number;
    channel: CampaignChannels.VOICE_CALL;
    cohortId?: string;
    content: {
        id: number;
        name: string;
    };
    name: string;
    recurrenceFrequency?: SendRecurrenceFrequency;
    recurrenceDayOfWeek?: string | null;
    recurrenceOccurrencePosition?: number | null;
    scheduledEndTimestamp?: string;
    scheduledTimestamp: string;
    segmentId?: number;
};

export type SendInput =
    | DirectMailSendInput
    | ExitActionInput
    | EmailSendInput
    | SmsSendInput
    | SmsSendInputLegacy
    | VoiceCallSendInput;

export enum SplitTypes {
    MEETS = 'meets criteria',
    DOES_NOT_MEET = 'does not meet criteria',
    RANDOM_GROUP_1 = 'group 1',
    RANDOM_GROUP_2 = 'group 2',
}

export enum SplitActions {
    SEND = 'send',
    EXIT = 'exit',
}

// This type is for custom curations
export type DirectMailSendInputState = Omit<DirectMailSendInput, 'contentId'> & {
    content: {
        id: number;
        name: string;
    };
};

export enum ABTestType {
    CONTENT = 'content',
    SEND_TIME = 'send_time',
}

export type EmailSendInputState = Omit<EmailSendInput, 'segments'> & {
    hasRecommendedTime?: boolean;
    // Custom curations are tracking segments like we do in audience builder
    // because we're using part of the audience builder code
    segments?: SegmentFilterState<SegmentFilterEngagementFields>[];
    splitType?: SplitTypes;
    splitAction?: SplitActions;
    // Frontend-only: links this branch send to its splitNodes entry. Stripped before API call.
    splitNodeId?: string;
};

export type SmsSendInputState = Omit<SmsSendInput, 'smsContentId'> & {
    content: {
        id: number;
        name: string;
    };
    smsSenderProfileId?: number | null;
};

/**
 * Curation routes
 */
export const ConditionalSplitRoutes = [
    URLPaths.CURATIONS_CONDITIONAL_SPLIT,
    URLPaths.CURATIONS_CONDITIONAL_SPLIT_SEGMENT,
    URLPaths.CURATIONS_CONDITIONAL_SPLIT_1_CONTENT,
    URLPaths.CURATIONS_CONDITIONAL_SPLIT_SCHEDULE,
    URLPaths.CURATIONS_CONDITIONAL_SPLIT_DELAY,
    URLPaths.CURATIONS_CONDITIONAL_SPLIT_SEGMENTS,
    URLPaths.CURATIONS_CONDITIONAL_SPLIT_SEGMENTS_MEETS_ACTION,
    URLPaths.CURATIONS_CONDITIONAL_SPLIT_SEGMENTS_MEETS_CONTENT,
    URLPaths.CURATIONS_CONDITIONAL_SPLIT_SEGMENTS_MEETS_NOT_ACTION,
    URLPaths.CURATIONS_CONDITIONAL_SPLIT_SEGMENTS_MEETS_NOT_CONTENT,
    URLPaths.CURATIONS_CONDITIONAL_SPLIT_PUBLISH,
];

export const getCreateCampaignRoute = (campaignKey: CampaignKey, nodeId: string) => {
    const campaignKeyKebabCase = getCamelToKebabCase(campaignKey);
    return `/curations/email/${campaignKeyKebabCase}/${nodeId}`;
};

/**
 * Types for partial props passed from different curations' NavBar to LegacyCampaignNavBar
 */
export type NodePropsPartial = Omit<Node, 'id' | 'position'>;
export type EdgePropsPartial = Omit<Edge, 'id' | 'source' | 'target'>;

export type ContentFields = {
    isAddOnSendClicked?: boolean;
    preHeader: string;
    senderProfileId: number;
    subjectLine: string;
    templateId: number;
};

export type MetaDataFields = {
    contentPreviewNodeId: string;
    name: string;
};

export type ScheduleFields = {
    isRecommendedTimeEnabled: boolean;
    scheduledTimestamp: string;
    parentAudienceDelayInterval?: string;
    recurrenceFrequency?: SendRecurrenceFrequency;
    scheduledEndTimestamp?: string;
    recurrenceDayOfWeek?: string | null;
    recurrenceOccurrencePosition?: number | null;
};

export enum DelayUnit {
    DAYS = 'days',
    HOURS = 'hours',
}

export type DelayFields = {
    delay: number;
    unit: DelayUnit;
};

export type ReEntryFields = {
    dimension: string;
    value: number;
};

// Campaign-level global exit criteria: the segment definition a contact must match,
// after entering, to be exited from the campaign. Mirrors ReEntryFields as a
// campaign-level lifecycle rule. The picker is only a "seed" — when a saved audience
// is chosen its definition is fetched and snapshotted directly into state (no id is
// kept), so the stored definition is the source of truth at publish/edit.
export type ExitCriteriaFields = SegmentDefinition;

/**
 * A lightweight topology item returned by the published campaign API in `campaign.structure`.
 * Contains only the fields needed to reconstruct the graph (parent references, split metadata,
 * time delay). Full send data (contentSnapshot, segmentDefinition, etc.) lives in campaign.sends.
 */
export type CampaignStructureItem = {
    id: number;
    channel: CampaignChannels;
    sendIndex?: number;
    parentId?: number;
    splitType?: SplitTypes;
    splitAction?: SplitActions;
    parentAudienceDelayInterval?: string;
    segmentId?: number;
    cohortId?: string;
    name?: string;
};

/**
 * The set of visual block types that can appear in the campaign graph.
 */
export type NodeType = 'audience' | 'send' | 'time_delay' | 'conditional_split' | 'ab_test' | 'exit' | 'random_split';

/**
 * Data payload for a graph node. Fields present depend on `NodeType`:
 * - audience:           segmentId, cohortId
 * - send:               sendId, sendIndex, channel, sendName
 * - time_delay:         interval, sendIndex (index of the delayed send — for navigation)
 * - conditional_split:  segmentDefinition, segments, splitSegmentId, sendIndex (MEETS send index)
 * - ab_test:            abTestConfig
 * - exit:               null
 */
export type GraphNodeData = {
    abTestConfig?: ABTestConfig;
    channel?: CampaignChannels;
    cohortId?: string;
    interval?: string;
    segmentDefinition?: SegmentDefinition;
    segmentId?: number;
    segments?: SegmentEngagement[];
    sendId?: number;
    sendIndex?: number;
    sendName?: string;
    splitAction?: SplitActions;
    splitSegmentId?: number;
    splitType?: SplitTypes;
    templateId?: number;
} | null;

export type GraphNode = {
    id: string;
    type: NodeType;
    data: GraphNodeData;
};

export type GraphEdge = {
    id: string;
    source: string;
    target: string;
    label?: 'meets' | 'does_not_meet' | 'group_1' | 'group_2';
};

export type CampaignGraph = {
    nodes: GraphNode[];
    edges: GraphEdge[];
};
